import { useState } from "react";
import { LineChart, Minus, Plus, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime, rupiah } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PinjamSaldoBanner } from "./PinjamSaldoBanner";
import { VoucherChatSetup } from "./VoucherChatSetup";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { DatePicker } from "./DatePicker";

export function AgentMutasiActions({ agent, user, testid }) {
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role === "admin";
  const [adjust, setAdjust] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ amount: "", note: "", pin: "" });
  const [mutasiOpen, setMutasiOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [mutasiDate, setMutasiDate] = useState("");
  const [mutasiTo, setMutasiTo] = useState("");
  const [mutasiFilter, setMutasiFilter] = useState("all");
  const [balance, setBalance] = useState(0);
  const [topupForm, setTopupForm] = useState({ pay_type: "Tunai", amount: "", note: "", pin: "" });
  const [debtKey, setDebtKey] = useState(0);
  const [busy, setBusy] = useState(false);

  if (!agent?.code || !user) {
    return <span className="inline-block text-[10px] text-muted-foreground" data-testid={`${testid}-noakun`}>-</span>;
  }

  const verifyPin = (pin) => api.post("/auth/verify-pin", { pin });

  const openAdjust = (mode) => {
    setBalance(user.balance || 0);
    setAdjustForm({ amount: "", note: "", pin: "" });
    setAdjust(mode);
  };

  const submitAdjust = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyPin(adjustForm.pin);
      const amt = Number(adjustForm.amount);
      const payload = adjust === "sub"
        ? { amount: amt, mode: "sub", note: adjustForm.note || "Potong saldo manual (Laporan Transaksi)" }
        : { amount: balance + amt, mode: "set", note: adjustForm.note || "Tambah saldo manual (Laporan Transaksi)" };
      const { data } = await api.put(`/users/${user.id}/balance`, payload);
      setBalance(data.balance);
      toast.success(`Saldo ${agent.name} kini ${rupiah(data.balance)}`);
      setAdjust(null);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const loadMutasi = (from, to = mutasiTo) => {
    setMutasiDate(from || "");
    setMutasiTo(to || "");
    const params = { user_id: user.id };
    if (from && to) { params.date_from = from; params.date_to = to; } else if (from) { params.date = from; }
    api.get("/balance/logs", { params })
      .then((r) => setLogs(r.data))
      .catch(() => setLogs([]));
  };

  const openMutasi = () => {
    setBalance(user.balance || 0);
    setTopupForm({ pay_type: "Tunai", amount: "", note: "", pin: "" });
    setMutasiOpen(true);
    setLogs([]);
    loadMutasi("");
  };

  const submitTopup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyPin(topupForm.pin);
      const amt = Number(topupForm.amount);
      if (topupForm.pay_type === "Hutang Saldo") {
        await api.post("/debts", { email: user.email, amount: amt, note: topupForm.note || `bon per ${new Date().toLocaleDateString("id-ID")}` });
        const { data: fresh } = await api.get(`/debts/summary`, { params: { user_id: user.id } });
        setBalance(balance + amt);
        toast.success(`Saldo ${agent.name} +${rupiah(amt)} sebagai HUTANG. Total hutang: ${rupiah(fresh.total)}`);
        setTopupForm({ pay_type: "Tunai", amount: "", note: "", pin: "" });
        setDebtKey((k) => k + 1);
        loadMutasi(mutasiDate);
        return;
      }
      const note = `[${topupForm.pay_type}] ${topupForm.note || "isi saldo"}`;
      const { data } = await api.put(`/users/${user.id}/balance`, { amount: balance + amt, mode: "set", note });
      setBalance(data.balance);
      toast.success(`Saldo ${agent.name} kini ${rupiah(data.balance)}`);
      setTopupForm({ pay_type: "Tunai", amount: "", note: "", pin: "" });
      loadMutasi(mutasiDate);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const mutasiIn = logs.reduce((a, l) => a + Math.max(0, l.amount), 0);
  const mutasiOut = logs.reduce((a, l) => a + Math.min(0, l.amount), 0);
  const mutasiKomisi = logs.filter((l) => /komisi|insentive|incentive/i.test(l.note || "")).reduce((a, l) => a + l.amount, 0);

  return (
    <>
      <div className="inline-flex items-center gap-1" data-testid={testid}>
        <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-rose-400 hover:bg-rose-500/10" title="Kurangi saldo" onClick={() => openAdjust("sub")} data-testid={`${testid}-sub`}><Minus className="w-3.5 h-3.5" /></Button>
        <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-emerald-400 hover:bg-emerald-500/10" title="Tambah saldo" onClick={() => openAdjust("add")} data-testid={`${testid}-add`}><Plus className="w-3.5 h-3.5" /></Button>
        <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-sky-400 hover:bg-sky-500/10" title="Mutasi saldo" onClick={openMutasi} data-testid={`${testid}-logs`}><LineChart className="w-3.5 h-3.5" /></Button>
        <VoucherChatSetup userId={user?.id} agentCode={agent?.code} testid={testid} />
      </div>

      <Dialog open={!!adjust} onOpenChange={() => setAdjust(null)}>
        <DialogContent data-testid={`${testid}-dialog-adjust`}>
          <DialogHeader><DialogTitle>{adjust === "sub" ? "Kurangi" : "Tambah"} Saldo Manual: {agent.name}</DialogTitle></DialogHeader>
          <form onSubmit={submitAdjust} className="space-y-3">
            <p className="text-xs text-muted-foreground">Akun: <span className="font-mono">{user.email}</span> • Saldo saat ini: <span className="font-mono font-semibold text-foreground" data-testid={`${testid}-adjust-balance`}>{rupiah(balance)}</span></p>
            <div className="space-y-1.5"><Label>Nominal (Rp)</Label><Input type="number" min="1" value={adjustForm.amount} onChange={(e) => setAdjustForm({ ...adjustForm, amount: e.target.value })} required placeholder="50000" data-testid={`${testid}-adjust-amount`} /></div>
            {adjust === "sub" && Number(adjustForm.amount) > Number(balance || 0) && <p className="text-xs rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-500 px-2 py-1.5" data-testid={`${testid}-adjust-minus-warning`}>Saldo akan menjadi <b>MINUS {rupiah(Number(adjustForm.amount) - Number(balance || 0))}</b>. Agen akan diberi tahu lewat chat dan wajib setor untuk melunasi.</p>}
            <div className="space-y-1.5"><Label>Catatan (opsional)</Label><Input value={adjustForm.note} onChange={(e) => setAdjustForm({ ...adjustForm, note: e.target.value })} data-testid={`${testid}-adjust-note`} /></div>
            <div className="space-y-1.5"><Label>PIN Admin (wajib)</Label><Input type="password" inputMode="numeric" value={adjustForm.pin} onChange={(e) => setAdjustForm({ ...adjustForm, pin: e.target.value })} required placeholder="Masukkan PIN" data-testid={`${testid}-adjust-pin`} /></div>
            <Button type="submit" variant={adjust === "sub" ? "destructive" : "default"} className="w-full" disabled={busy} data-testid={`${testid}-adjust-confirm`}>
              {busy ? "Memproses..." : adjust === "sub" ? "Kurangi Saldo" : "Tambah Saldo"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={mutasiOpen} onOpenChange={setMutasiOpen}>
        <DialogContent className="sm:max-w-5xl max-h-[92vh] overflow-y-auto p-0 gap-0" data-testid={`${testid}-dialog-mutasi`}>
          <DialogHeader className="sr-only"><DialogTitle>Saldo & Mutasi {agent.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-[280px_1fr]">
            <div className="border-b md:border-b-0 md:border-r border-border">
              <div className="px-4 py-3 bg-[#F04E23] text-white font-heading font-bold text-sm">Tambah Saldo</div>
              <form onSubmit={submitTopup} className="p-4 space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">Tipe Pembayaran</Label>
                  <select value={topupForm.pay_type} onChange={(e) => setTopupForm({ ...topupForm, pay_type: e.target.value })} data-testid={`${testid}-topup-paytype`}
                    className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                    {["Tunai", "Transfer Bank", "QRIS", "Hutang Saldo"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Nominal Saldo</Label>
                  <Input type="number" min="1" className="h-8 text-xs font-mono" value={topupForm.amount} onChange={(e) => setTopupForm({ ...topupForm, amount: e.target.value })} required placeholder="50000" data-testid={`${testid}-topup-amount`} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Keterangan</Label>
                  <Input className="h-8 text-xs" value={topupForm.note} onChange={(e) => setTopupForm({ ...topupForm, note: e.target.value })} placeholder="isi saldo" data-testid={`${testid}-topup-note`} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">PIN Admin (wajib)</Label>
                  <Input type="password" inputMode="numeric" className="h-8 text-xs" value={topupForm.pin} onChange={(e) => setTopupForm({ ...topupForm, pin: e.target.value })} required placeholder="Masukkan PIN" data-testid={`${testid}-topup-pin`} />
                </div>
                <Button type="submit" className="w-full h-9 bg-[#F04E23] hover:bg-[#d94420] text-white text-xs font-bold" disabled={busy} data-testid={`${testid}-topup-proses`}>
                  {busy ? "Memproses..." : "Proses"}
                </Button>
              </form>
            </div>
            <div className="min-w-0">
              <div className="px-4 py-3 bg-sky-500/15 border-b border-border font-heading font-bold text-sm text-sky-400">Informasi Agen</div>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-start">
                  <div className="rounded-xl border border-border overflow-hidden text-xs">
                    <div className="px-3 py-2 bg-accent/40 font-semibold flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-sky-400" /> Data Agen</div>
                    <div className="divide-y divide-border">
                      <div className="grid grid-cols-[110px_1fr] px-3 py-1.5"><span className="text-muted-foreground">Kode Agen</span><span className="font-mono font-bold text-[#F04E23]" data-testid={`${testid}-agent-code`}>{agent.code}</span></div>
                      <div className="grid grid-cols-[110px_1fr] px-3 py-1.5"><span className="text-muted-foreground">Email</span><span className="font-mono break-all">{user.email || "-"}</span></div>
                      <div className="grid grid-cols-[110px_1fr] px-3 py-1.5"><span className="text-muted-foreground">Nama</span><span className="font-medium">{agent.name}</span></div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 min-w-44">
                    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-right">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Saldo Sekarang :</div>
                      <div className={`font-mono font-bold text-2xl ${balance < 0 ? "text-rose-400" : "text-emerald-400"}`} data-testid={`${testid}-saldo`}>{rupiah(balance).replace("Rp", "").trim()}</div>
                    </div>
                    <PinjamSaldoBanner key={debtKey} userId={user.id} canEdit compact testid={`${testid}-pinjam`} />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-xs font-semibold flex items-center gap-1.5"><LineChart className="w-3.5 h-3.5 text-sky-400" /> Mutasi Saldo Agen</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex rounded-lg border border-border overflow-hidden text-[10px] font-mono" data-testid={`${testid}-mutasi-filter`}>
                        {[["all", "Semua", ""], ["masuk", "Masuk", "text-sky-400"], ["keluar", "Keluar", "text-rose-400"], ["komisi", "Komisi", "text-emerald-400"]].map(([k, lbl, cls]) => (
                          <button key={k} type="button" onClick={() => setMutasiFilter(k)} className={`px-2.5 py-1 transition-colors ${mutasiFilter === k ? `bg-accent font-bold ${cls}` : "text-muted-foreground hover:bg-accent/50"}`} data-testid={`${testid}-mutasi-filter-${k}`}>{lbl}</button>
                        ))}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <DatePicker className="h-9 w-40 text-xs cursor-pointer [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:p-1 [&::-webkit-calendar-picker-indicator]:opacity-80" value={mutasiDate} onChange={(v) => loadMutasi(v, mutasiTo)} placeholder="Dari tanggal (kosongkan = terakhir)" testid={`${testid}-mutasi-date`} />
                        <span className="text-[10px] text-muted-foreground">s/d</span>
                        <DatePicker className="h-9 w-40 text-xs cursor-pointer [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:p-1 [&::-webkit-calendar-picker-indicator]:opacity-80" value={mutasiTo} min={mutasiDate } onChange={(v) => loadMutasi(mutasiDate, v)} placeholder="Sampai tanggal (kosongkan = 1 hari)" testid={`${testid}-mutasi-date-to`} />
                      </div>
                    </div>
                  </div>
                  {logs.length > 0 && (
                    <div className="flex gap-4 justify-end flex-wrap text-[11px] font-mono">
                      {(mutasiDate || mutasiTo) && <span className="text-muted-foreground">Periode: {fmtDate(mutasiDate || mutasiTo)}{mutasiTo && mutasiDate && mutasiTo !== mutasiDate ? ` – ${fmtDate(mutasiTo)}` : ""}</span>}
                      <span className="text-emerald-400" data-testid={`${testid}-total-in`}>Masuk: +{rupiah(mutasiIn)}</span>
                      <span className="text-rose-400" data-testid={`${testid}-total-out`}>Keluar: {rupiah(mutasiOut)}</span>
                      <span className="text-emerald-300" data-testid={`${testid}-total-komisi`}>Komisi: +{rupiah(mutasiKomisi)}</span>
                      <span className="text-sky-400">{logs.length} transaksi</span>
                    </div>
                  )}
                  <div className="rounded-xl border border-border overflow-x-auto">
                    <Table className="min-w-[640px]" data-testid={`${testid}-mutasi-table`}>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Tanggal</TableHead>
                          <TableHead className="text-xs">Tipe</TableHead>
                          <TableHead className="text-xs">Deskripsi</TableHead>
                          <TableHead className="text-xs text-right">Keluar</TableHead>
                          <TableHead className="text-xs text-right">Masuk</TableHead>
                          <TableHead className="text-xs text-right">Saldo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(() => {
                          let bal = balance;
                          const rows = logs.map((l) => {
                            const rowBal = bal;
                            bal -= l.amount;
                            const isKomisi = /komisi|insentive|incentive/i.test(l.note || "");
                            const kind = isKomisi ? "komisi" : l.amount >= 0 ? "masuk" : "keluar";
                            return { l, rowBal, isKomisi, kind };
                          }).filter((r) => mutasiFilter === "all" || r.kind === mutasiFilter);
                          if (!rows.length && logs.length) return <TableRow><TableCell colSpan={6} className="text-center py-8 text-xs text-muted-foreground" data-testid={`${testid}-mutasi-filter-empty`}>Tidak ada mutasi {mutasiFilter} pada daftar ini</TableCell></TableRow>;
                          return rows.map(({ l, rowBal, isKomisi }) => {
                            const tipe = isKomisi ? "Komisi" : l.amount >= 0 ? "MASUK" : "KELUAR";
                            const tipeCls = isKomisi ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : l.amount >= 0 ? "bg-sky-500/10 text-sky-400 border-sky-500/20" : "bg-rose-500/10 text-rose-400 border-rose-500/20";
                            return (
                              <TableRow key={l.id} data-testid={`${testid}-log-${l.id}`}>
                                <TableCell className="text-[11px] font-mono whitespace-nowrap text-muted-foreground">{fmtDateTime(l.created_at)}</TableCell>
                                <TableCell><span className={`px-2 py-0.5 rounded-full text-[10px] font-mono border ${tipeCls}`}>{tipe}</span></TableCell>
                                <TableCell className="text-[11px] break-words max-w-64">
                                  {isSuperAdmin && l.voucher_link ? <a href={l.voucher_link} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline underline-offset-2" title="Buka batch voucher ini" data-testid={`mutasi-voucher-link-${l.id}`}>{l.note} ↗</a> : l.note}
                                  <span className="block text-[10px] text-muted-foreground font-mono">oleh {l.by_label || l.by || "sistem"}{l.batch_id && <span className="ml-1 text-sky-500/70">• {l.batch_id}</span>}</span>
                                </TableCell>
                                <TableCell className="text-right font-mono text-[11px] text-rose-400">{l.amount < 0 ? rupiah(-l.amount).replace("Rp", "").trim() : "0"}</TableCell>
                                <TableCell className="text-right font-mono text-[11px] text-emerald-400">{l.amount >= 0 ? rupiah(l.amount).replace("Rp", "").trim() : "0"}{l.pct && <span className="ml-1 px-1 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-[9px] font-semibold" data-testid={`${testid}-pct-${l.id}`}>{l.pct}</span>}</TableCell>
                                <TableCell className="text-right font-mono text-[11px] font-semibold">{rupiah(rowBal).replace("Rp", "").trim()}</TableCell>
                              </TableRow>
                            );
                          });
                        })()}
                        {!logs.length && <TableRow><TableCell colSpan={6} className="text-center py-8 text-xs text-muted-foreground" data-testid={`${testid}-mutasi-empty`}>Tidak ada mutasi{mutasiDate || mutasiTo ? " pada periode ini" : ""}</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
